import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import { io, Socket } from "socket.io-client";
import { chatService, getAccessToken } from "@b2b/services";

type ChatMessagePayload = Awaited<ReturnType<typeof chatService.sendMessageHttp>>;

type ChatSocketEvents = {
  onMessageNew?: (payload: any) => void;
  onMessagesRead?: (payload: { conversationId: string; readByUserId: string }) => void;
  onPresenceSnapshot?: (snapshot: Record<string, boolean>) => void;
  onPresenceUpdate?: (payload: { userId: string; online: boolean }) => void;
  onError?: (payload: any) => void;
};

const getWsBaseUrl = () => {
  const raw = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";
  return raw.endsWith("/") ? raw.slice(0, -1) : raw;
};

function attachEventListeners(socket: Socket, eventsRef: MutableRefObject<ChatSocketEvents | undefined>) {
  socket.off("message:new");
  socket.off("messages:read");
  socket.off("presence:snapshot");
  socket.off("presence:update");
  socket.off("error");

  socket.on("message:new", (payload) => eventsRef.current?.onMessageNew?.(payload));
  socket.on("messages:read", (payload) => eventsRef.current?.onMessagesRead?.(payload));
  socket.on("presence:snapshot", (snapshot) => eventsRef.current?.onPresenceSnapshot?.(snapshot));
  socket.on("presence:update", (payload) => eventsRef.current?.onPresenceUpdate?.(payload));
  socket.on("error", (payload) => eventsRef.current?.onError?.(payload));
}

export function useChatSocket(
  events?: ChatSocketEvents,
  activeConversationIdRef?: MutableRefObject<string | null>
) {
  const socketRef = useRef<Socket | null>(null);
  const eventsRef = useRef<ChatSocketEvents | undefined>(events);
  const activeRef = activeConversationIdRef;
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  const connect = useCallback(async () => {
    if (socketRef.current?.connected) {
      attachEventListeners(socketRef.current, eventsRef);
      return socketRef.current;
    }

    const token = await getAccessToken();
    const baseUrl = getWsBaseUrl();

    const socket = io(`${baseUrl}/chat`, {
      transports: ["websocket"],
      auth: { token },
    });

    socket.on("connect", () => {
      setIsConnected(true);
      const id = activeRef?.current;
      if (id) {
        socket.emit("conversation:join", { conversationId: id }, () => {});
      }
    });
    socket.on("disconnect", () => setIsConnected(false));

    attachEventListeners(socket, eventsRef);

    socketRef.current = socket;
    return socket;
  }, [activeRef]);

  const disconnect = useCallback(() => {
    socketRef.current?.disconnect();
    socketRef.current = null;
    setIsConnected(false);
  }, []);

  const joinConversation = useCallback(async (conversationId: string) => {
    const socket = await connect();
    return new Promise<{ counterpartOnline?: boolean }>((resolve, reject) => {
      const t = setTimeout(() => reject(new Error("join timeout")), 12000);
      socket.emit("conversation:join", { conversationId }, (response: any) => {
        clearTimeout(t);
        if (response?.success) {
          resolve({ counterpartOnline: response.counterpartOnline });
        } else {
          reject(new Error(response?.message || "join failed"));
        }
      });
    });
  }, [connect]);

  const sendMessage = useCallback(
    async (payload: {
      conversationId: string;
      text?: string;
      attachments?: { key: string }[];
    }): Promise<ChatMessagePayload> => {
      const socket = await connect();
      const viaSocket = () =>
        new Promise<ChatMessagePayload>((resolve, reject) => {
          const t = setTimeout(() => reject(new Error("send timeout")), 15000);
          socket.emit("message:send", payload, (response: any) => {
            clearTimeout(t);
            if (response?.success && response?.data) {
              resolve(response.data as ChatMessagePayload);
            } else {
              reject(new Error(response?.message || "send failed"));
            }
          });
        });

      try {
        return await viaSocket();
      } catch {
        return chatService.sendMessageHttp(payload.conversationId, {
          text: payload.text,
          attachments: payload.attachments,
        });
      }
    },
    [connect]
  );

  useEffect(() => {
    return () => disconnect();
  }, [disconnect]);

  return useMemo(
    () => ({
      isConnected,
      connect,
      disconnect,
      joinConversation,
      sendMessage,
    }),
    [isConnected, connect, disconnect, joinConversation, sendMessage]
  );
}
